import { Sparkles, Rocket, BookOpen, MessageCircle, X } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: () => void;
}

export function WelcomeModal({ isOpen, onClose, onStart }: WelcomeModalProps) {
  if (!isOpen) return null;

  const features = [
    {
      icon: Rocket,
      title: 'Créez votre premier agent',
      description: 'Un assistant IA personnalisé pour votre activité',
    },
    {
      icon: BookOpen,
      title: 'Enrichissez ses connaissances',
      description: 'Importez vos documents pour des réponses précises',
    },
    {
      icon: MessageCircle,
      title: 'Testez et personnalisez',
      description: 'Ajustez le comportement selon vos besoins',
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-zinc-400 hover:text-white transition-colors z-10"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header with gradient */}
        <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-emerald-500/20 via-emerald-600/10 to-transparent">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30">
              <Sparkles className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Bienvenue sur Totemium !
              </h2>
              <p className="text-sm text-emerald-400">
                Votre Guide de démarrage
              </p>
            </div>
          </div>

          <p className="text-zinc-300 text-sm leading-relaxed">
            Je suis <strong className="text-emerald-400">Totem Guide</strong>, votre assistant personnel. 
            Je vais vous accompagner pas à pas pour créer votre premier agent IA et exploiter 
            tout le potentiel de la plateforme.
          </p>
        </div>

        {/* Features */}
        <div className="px-6 py-4 space-y-3">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
            Ce que nous allons faire ensemble
          </p>
          {features.map((feature, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50"
            >
              <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/10">
                <feature.icon className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white">
                  {feature.title}
                </h3>
                <p className="text-xs text-zinc-400">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-zinc-800/30 border-t border-zinc-800 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            Plus tard
          </button>
          <button
            onClick={onStart}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
          >
            <Rocket className="w-4 h-4" />
            C'est parti !
          </button>
        </div>
      </div>
    </div>
  );
}

export default WelcomeModal;
